import React, { useState } from 'react'
import { DatePicker, Input, Select, Table, Tag, Tooltip } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import dayjs from 'dayjs'
import { Moment } from 'moment'
import Button from '@/components/button/button'
import { Modal as SgModal } from '@/components/dialog/modal'
import SearchInput from '@/components/search-input/search-input'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { createSvipEntitlementRefund, getSvipPurchaseOrders } from '../api'
import SettingPage, { SettingTableContainer } from '../member-setting/setting-page'
import { getMemberTablePagination, getTableTotal, MEMBER_TABLE_PAGE_SIZE } from '../table-utils'
import { formatSvipExpireTime, getSvipRefundStatusPresentation } from './svip-purchase-record-utils'

const itemTypeMap: Record<number, string> = { 0: '月卡', 1: '季卡', 2: '年卡' }

const SvipPurchaseRecord: React.FC = () => {
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(MEMBER_TABLE_PAGE_SIZE)
    const [keyword, setKeyword] = useState('')
    const [searchKey, setSearchKey] = useState(0)
    const [itemTypes, setItemTypes] = useState<number[]>([])
    const [date, setDate] = useState<string[]>([])
    const [dateValue, setDateValue] = useState<[Moment | null, Moment | null] | null>(null)
    const [refundRecord, setRefundRecord] = useState<any>(null)
    const [refundReason, setRefundReason] = useState('')
    const req = useRequest(
        () =>
            getSvipPurchaseOrders({
                page,
                limit: pageSize,
                keyword: keyword || undefined,
                from_date: date[0],
                to_date: date[1],
                item_types: itemTypes.length ? itemTypes : undefined,
            }),
        {
            refreshDeps: [page, pageSize, keyword, date, itemTypes],
            onError: e => $msg('error', e.message || '获取付费会员购买记录失败'),
        },
    )
    const refundReq = useRequest(createSvipEntitlementRefund, {
        manual: true,
        onSuccess: refundOrder => {
            setRefundRecord(null)
            setRefundReason('')
            req.refresh()
            if (refundOrder.overall_status === 'succeeded') {
                $msg('success', '退款成功，会员有效期已扣减')
            } else if (refundOrder.overall_status === 'unknown') {
                $msg('warning', refundOrder.provider_message || '退款结果待确认，请勿重复操作', 3)
            } else if (refundOrder.overall_status === 'entitlement_failed') {
                $msg('error', refundOrder.provider_message || '资金已退回，但会员权益撤销异常', 3)
            } else if (refundOrder.overall_status === 'failed') {
                $msg('error', refundOrder.provider_message || '退款失败，请稍后重试', 3)
            } else {
                $msg('info', '退款处理中，请稍后刷新查看')
            }
        },
        onError: e => $msg('error', e.message || '退款失败，请稍后重试', 3),
    })
    const reset = () => {
        setPage(1)
        setKeyword('')
        setSearchKey(value => value + 1)
        setItemTypes([])
        setDate([])
        setDateValue(null)
    }
    const columns: ColumnsType<any> = [
        {
            title: '购买时间',
            dataIndex: 'create_time',
            width: 170,
            render: value => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
        },
        { title: '订单号', dataIndex: 'order_no', width: 220, render: value => value || '--' },
        {
            title: '会员',
            dataIndex: 'member',
            width: 170,
            render: value => value?.name || value?.user?.nickname || '--',
        },
        { title: '手机号', dataIndex: ['member', 'phone'], width: 150, render: value => value || '--' },
        { title: '购买类型', dataIndex: 'item_type', width: 110, render: value => itemTypeMap[value] || '--' },
        { title: '支付金额', dataIndex: 'payment_amount', width: 120, render: value => `${value || 0} 元` },
        {
            title: '支付方式',
            dataIndex: 'payment_type',
            width: 140,
            render: value => value?.custom_payment?.name || value?.payment_type_text || '--',
        },
        {
            title: '退款状态',
            dataIndex: 'refund_status',
            width: 190,
            render: value => {
                const status = getSvipRefundStatusPresentation(value)

                return <Tag color={status.color}>{status.text}</Tag>
            },
        },
        {
            title: '到期时间',
            dataIndex: 'svip_expire_time',
            width: 170,
            render: value => formatSvipExpireTime(value, date => dayjs(date).format('YYYY-MM-DD HH:mm:ss')),
        },
        {
            title: '操作',
            fixed: 'right',
            width: 110,
            render: (_value, record) => {
                if (record.can_refund) {
                    return (
                        <Auth
                            perssion={24}
                            onClick={() => {
                                setRefundReason('')
                                setRefundRecord(record)
                            }}
                        >
                            <Button types="error">退款</Button>
                        </Auth>
                    )
                }

                return (
                    <Tooltip title={record.unrefundable_reason || '当前记录不可退款'}>
                        <span>/</span>
                    </Tooltip>
                )
            },
        },
    ]
    const refundModal = refundRecord ? (
        <SgModal
            isShow
            width={520}
            type="danger"
            title="付费会员退款"
            okText="确认退款"
            cancelText="取消"
            loading={refundReq.loading}
            onClose={() => !refundReq.loading && setRefundRecord(null)}
            onCancel={() => !refundReq.loading && setRefundRecord(null)}
            onConfirm={() =>
                refundReq.run({
                    source_order_id: refundRecord.purchase_order_id,
                    refund_reason: refundReason.trim() || undefined,
                })
            }
        >
            <div data-font-size-18 data-font-weight-600 data-color-000>
                确认退回 {Number(refundRecord.payment_amount || 0).toFixed(2)} 元吗？
            </div>
            <div data-font-size-14 data-color-666 data-margin-top-12>
                退款将按原支付方式退回，成功后会员有效期扣减 {refundRecord.duration_months || 0} 个月。
            </div>
            <div data-font-size-14 data-color-666 data-margin-top-8>
                到期时间：
                {formatSvipExpireTime(refundRecord.svip_expire_time, date => dayjs(date).format('YYYY-MM-DD HH:mm:ss'))}
                {' → '}
                {formatSvipExpireTime(refundRecord.estimated_svip_expire_time_after_refund, date =>
                    dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
                )}
            </div>
            <Input.TextArea
                value={refundReason}
                maxLength={256}
                rows={3}
                placeholder="退款原因（选填）"
                style={{ marginTop: 16 }}
                onChange={event => setRefundReason(event.target.value)}
            />
            <div data-font-size-13 data-color-ff4343 data-margin-top-12>
                退款发起后不可重复操作，请确认金额和会员有效期无误。
            </div>
        </SgModal>
    ) : null

    return (
        <SettingPage
            title="付费会员购买记录"
            className="member-basic-setting"
            bodyClassName="member-basic-setting__body"
            append={refundModal}
            extra={
                <>
                    <DatePicker.RangePicker
                        value={dateValue}
                        allowClear
                        suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                        onChange={(value, dateString) => {
                            setPage(1)
                            setDateValue(value)
                            setDate(dateString[0] ? dateString : [])
                        }}
                    />
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="购买类型"
                        value={itemTypes}
                        style={{ width: 210 }}
                        options={Object.keys(itemTypeMap).map(value => ({
                            value: Number(value),
                            label: itemTypeMap[Number(value)],
                        }))}
                        onChange={value => {
                            setPage(1)
                            setItemTypes(value)
                        }}
                    />
                    <div className="member-basic-setting__search">
                        <SearchInput
                            key={searchKey}
                            placeholder="输入订单号"
                            onSearch={value => {
                                setPage(1)
                                setKeyword(value)
                            }}
                            onDelete={() => {
                                setPage(1)
                                setKeyword('')
                            }}
                        />
                    </div>
                    <Button types="primary" onClick={reset}>
                        重置
                    </Button>
                    <Button types="primary" loading={req.loading} onClick={() => req.refresh()}>
                        刷新
                    </Button>
                </>
            }
        >
            <SettingTableContainer className="member-basic-setting__panel">
                <Table
                    bordered
                    rowKey={(record, index) => record.purchase_order_id || record.order_no || index}
                    loading={req.loading}
                    columns={columns}
                    dataSource={req.data?.records || []}
                    pagination={getMemberTablePagination({
                        current: page,
                        pageSize,
                        total: getTableTotal(req.data?.pagination),
                        onChange: setPage,
                        onShowSizeChange: (nextPage, nextSize) => {
                            setPage(nextPage)
                            setPageSize(nextSize)
                        },
                    })}
                    scroll={{ x: 'max-content' }}
                />
            </SettingTableContainer>
        </SettingPage>
    )
}

export default SvipPurchaseRecord
